import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Building, Plus, Pencil, Trash2, Loader2, Users, Globe, Clock3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { getRoleLabel } from '../lib/role-labels';
import { getApiBaseUrl } from '../lib/api-base-url';

const API_URL = getApiBaseUrl();

interface Tenant {
  id: number;
  name: string;
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  emailFatturazione: string;
  isActive: boolean;
  language: string;
  companyAddress?: string;
  vatNumber?: string;
  pec?: string;
  commercialEntityId?: number | null;
  commercialEntityCreatedWithTenant?: boolean;
  activationStatus?: 'activated' | 'awaiting_activation';
}

interface TenantFormData {
  name: string;
  logoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  emailFatturazione: string;
  language: string;
  companyAddress: string;
  vatNumber: string;
  pec: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  tenantId: number;
  isActive: boolean;
}

interface ActivationStep {
  key: string;
  title: string;
  status: 'completed' | 'in_progress' | 'pending' | 'failed';
  at: string | null;
}

interface TenantActivationStatus {
  tenantId: number;
  tenantName: string;
  overallStatus: string;
  invitation: {
    id: number;
    email: string;
    createdAt: string;
    expiresAt: string;
    usedAt: string | null;
    revokedAt: string | null;
    state: string;
  } | null;
  adminAccount: {
    id: number;
    email: string;
    createdAt: string;
  } | null;
  steps: ActivationStep[];
}

const AdminTenantsList = () => {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [formData, setFormData] = useState<TenantFormData>({
    name: '',
    logoUrl: '',
    primaryColor: '#f97316',
    secondaryColor: '#ea580c',
    emailFatturazione: '',
    language: 'en',
    companyAddress: '',
    vatNumber: '',
    pec: '',
  });
  const [userManagementOpen, setUserManagementOpen] = useState(false);
  const [selectedTenantUsers, setSelectedTenantUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [pendingUserRoles, setPendingUserRoles] = useState<Record<number, string>>({});
  const [savingUserRoles, setSavingUserRoles] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
  const [deleteAlsoCommercial, setDeleteAlsoCommercial] = useState(false);
  const [deletingTenant, setDeletingTenant] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [tenantStatus, setTenantStatus] = useState<TenantActivationStatus | null>(null);
  const [resendingInvitation, setResendingInvitation] = useState(false);
  const [statusTenantId, setStatusTenantId] = useState<number | null>(null);

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/tenants`);
      setTenants(response.data);
    } catch (err) {
      setError(t('pages.tenants.errors.fetch', 'Failed to fetch tenants'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchTenantUsers = async (tenantId: number) => {
    try {
      setLoadingUsers(true);
      const response = await axios.get(
        `${API_URL}/users/super-admin/tenant/${tenantId}?includeInactive=true`
      );
      const users = (response.data as User[])
        .map((user) => ({
          ...user,
          role: (user.role || '').trim().toUpperCase(),
        }))
        .filter((user) => user.role !== 'SUPER_ADMIN')
        .sort((a, b) => {
        if (a.isActive !== b.isActive) {
          return a.isActive ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
      setSelectedTenantUsers(users);
      setPendingUserRoles(
        Object.fromEntries(users.map((u) => [u.id, u.role]))
      );
      setUserManagementOpen(true);
    } catch (err) {
      setError(t('pages.tenants.errors.fetchUsers', 'Failed to fetch tenant users'));
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSaveUserRoles = async () => {
    const currentTenantId = selectedTenantUsers[0]?.tenantId;
    if (!currentTenantId) {
      return;
    }

    const changedUsers = selectedTenantUsers.filter((user) => {
      const nextRole = pendingUserRoles[user.id] ?? user.role;
      return nextRole !== user.role;
    });

    if (changedUsers.length === 0) {
      return;
    }

    try {
      setSavingUserRoles(true);

      await Promise.all(
        changedUsers.map((user) =>
          axios.patch(
            `${API_URL}/users/super-admin/${user.id}/role`,
            { role: pendingUserRoles[user.id] ?? user.role }
          )
        )
      );

      await fetchTenantUsers(currentTenantId);
    } catch (err) {
      setError(t('pages.tenants.errors.updateRole', 'Failed to update user role'));
    } finally {
      setSavingUserRoles(false);
    }
  };

  const hasPendingRoleChanges = selectedTenantUsers.some((user) => {
    const nextRole = pendingUserRoles[user.id] ?? user.role;
    return nextRole !== user.role;
  });

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleEdit = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name,
      logoUrl: tenant.logoUrl || '',
      primaryColor: tenant.primaryColor,
      secondaryColor: tenant.secondaryColor,
      emailFatturazione: tenant.emailFatturazione,
      language: tenant.language,
      companyAddress: tenant.companyAddress || '',
      vatNumber: tenant.vatNumber || '',
      pec: tenant.pec || '',
    });
    setOpenDialog(true);
  };

  const handleCreate = () => {
    setSelectedTenant(null);
    setFormData({
      name: '',
      logoUrl: '',
      primaryColor: '#f97316',
      secondaryColor: '#ea580c',
      emailFatturazione: '',
      language: 'en',
      companyAddress: '',
      vatNumber: '',
      pec: '',
    });
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedTenant(null);
    setError('');
  };

  const handleDelete = (tenant: Tenant) => {
    setTenantToDelete(tenant);
    setDeleteAlsoCommercial(Boolean(tenant.commercialEntityCreatedWithTenant));
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setTenantToDelete(null);
    setDeleteAlsoCommercial(false);
  };

  const handleDeleteConfirm = async () => {
    if (!tenantToDelete) {
      return;
    }

    const canDeleteCommercial = Boolean(
      tenantToDelete.commercialEntityCreatedWithTenant && tenantToDelete.commercialEntityId,
    );

    try {
      setDeletingTenant(true);
      await axios.delete(`${API_URL}/tenants/${tenantToDelete.id}`, {
        params: {
          removeCommercialEntity: canDeleteCommercial && deleteAlsoCommercial,
        },
      });
      await fetchTenants();
      handleDeleteDialogClose();
    } catch (err: any) {
      const serverMessage = err?.response?.data?.message;
      setError(serverMessage || t('pages.tenants.errors.delete', 'Failed to delete tenant'));
    } finally {
      setDeletingTenant(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      if (selectedTenant) {
        await axios.patch(
          `${API_URL}/tenants/${selectedTenant.id}`,
          formData
        );
      } else {
        await axios.post(`${API_URL}/tenants`, formData);
      }
      await fetchTenants();
      handleClose();
    } catch (err: any) {
      const serverMessage = err?.response?.data?.message;
      setError(serverMessage || t('pages.tenants.errors.save', 'Failed to save tenant'));
    } finally {
      setSaving(false);
    }
  };

  const getStepBadgeVariant = (status: ActivationStep['status']): 'default' | 'secondary' | 'destructive' | 'outline' => {
    if (status === 'completed') return 'default';
    if (status === 'in_progress') return 'secondary';
    if (status === 'failed') return 'destructive';
    return 'outline';
  };

  const formatDateTime = (value?: string | null): string => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
  };

  const handleOpenStatus = async (tenant: Tenant) => {
    try {
      setLoadingStatus(true);
      setTenantStatus(null);
      setStatusTenantId(tenant.id);
      setStatusDialogOpen(true);
      const response = await axios.get(`${API_URL}/tenants/${tenant.id}/activation-status`);
      setTenantStatus(response.data);
    } catch (err) {
      setError(t('pages.tenants.errors.fetchStatus', 'Failed to fetch activation status'));
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleResendInvitation = async () => {
    if (!statusTenantId) return;
    try {
      setResendingInvitation(true);
      await axios.post(`${API_URL}/tenants/${statusTenantId}/resend-invitation`);
      // Refresh status after resend
      const response = await axios.get(`${API_URL}/tenants/${statusTenantId}/activation-status`);
      setTenantStatus(response.data);
      await fetchTenants();
    } catch (err) {
      setError(t('pages.tenants.errors.resendInvitation', 'Failed to resend activation email'));
    } finally {
      setResendingInvitation(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <Building className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('pages.tenants.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('pages.tenants.subtitle', 'Manage organization tenants')}
            </p>
          </div>
        </div>

        <Button onClick={handleCreate} className="w-full bg-orange-500 hover:bg-orange-600 gap-2 sm:w-auto">
          <Plus className="h-4 w-4" />
          {t('pages.tenants.actions.create')}
        </Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {/* Tenants Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {t('pages.tenants.list', 'Tenants')} ({tenants.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
            </div>
          ) : (
            <>
            <div className="space-y-3 md:hidden">
              {tenants.length === 0 && (
                <p className="py-8 text-center text-muted-foreground">{t('pages.tenants.noResults', 'No tenants found')}</p>
              )}
              {tenants.map((tenant) => (
                <div key={tenant.id} className="rounded-lg border p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{tenant.name}</p>
                      <p className="truncate text-sm text-muted-foreground">{tenant.emailFatturazione}</p>
                    </div>
                    <Badge variant={tenant.isActive ? 'default' : 'secondary'} className="shrink-0">
                      {tenant.isActive ? t('common.active') : t('common.inactive')}
                    </Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1">
                      <span className="h-5 w-5 rounded border" style={{ backgroundColor: tenant.primaryColor }} />
                      <span className="h-5 w-5 rounded border" style={{ backgroundColor: tenant.secondaryColor }} />
                    </span>
                    <Badge variant="outline" className="gap-1">
                      <Globe className="h-3 w-3" />
                      {tenant.language.toUpperCase()}
                    </Badge>
                    <Badge variant={tenant.commercialEntityId ? 'default' : 'secondary'}>
                      {tenant.commercialEntityId
                        ? t('pages.tenants.registryLink.linked', 'Linked')
                        : t('pages.tenants.registryLink.notLinked', 'Not linked')}
                    </Badge>
                  </div>
                  <div className="mt-2 flex justify-end gap-1 border-t pt-2">
                    <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => fetchTenantUsers(tenant.id)}>
                      <Users className="h-4 w-4" />
                      {t('pages.tenants.actions.manageUsers', 'Manage Users')}
                    </Button>
                    {tenant.activationStatus !== 'activated' && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenStatus(tenant)} title={t('pages.tenants.actions.viewStatus', 'View activation status')}>
                        <Clock3 className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(tenant)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDelete(tenant)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Table className="hidden md:table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('pages.tenants.columns.name')}</TableHead>
                  <TableHead>{t('pages.tenants.columns.email')}</TableHead>
                  <TableHead>{t('pages.tenants.columns.primaryColor')}</TableHead>
                  <TableHead>{t('pages.tenants.columns.secondaryColor')}</TableHead>
                  <TableHead>{t('pages.tenants.columns.language')}</TableHead>
                  <TableHead>{t('pages.tenants.registryLink.linkStatus', 'Registry')}</TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell className="font-medium">{tenant.name}</TableCell>
                    <TableCell className="text-muted-foreground">{tenant.emailFatturazione}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-6 rounded border"
                          style={{ backgroundColor: tenant.primaryColor }}
                        />
                        <span className="font-mono text-xs text-muted-foreground">
                          {tenant.primaryColor}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-6 rounded border"
                          style={{ backgroundColor: tenant.secondaryColor }}
                        />
                        <span className="font-mono text-xs text-muted-foreground">
                          {tenant.secondaryColor}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="gap-1">
                        <Globe className="h-3 w-3" />
                        {tenant.language.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={tenant.commercialEntityId ? 'default' : 'secondary'}>
                        {tenant.commercialEntityId
                          ? t('pages.tenants.registryLink.linked', 'Linked')
                          : t('pages.tenants.registryLink.notLinked', 'Not linked')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={tenant.isActive ? 'default' : 'secondary'}>
                        {tenant.isActive ? t('common.active') : t('common.inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => fetchTenantUsers(tenant.id)}
                          className="h-8 w-8"
                          title={t('pages.tenants.actions.manageUsers', 'Manage Users')}
                        >
                          <Users className="h-4 w-4" />
                        </Button>
                        {tenant.activationStatus !== 'activated' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenStatus(tenant)}
                            className="h-8 w-8"
                            title={t('pages.tenants.actions.viewStatus', 'View activation status')}
                          >
                            <Clock3 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(tenant)}
                          className="h-8 w-8"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(tenant)}
                          className="h-8 w-8 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {tenants.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      {t('pages.tenants.noResults', 'No tenants found')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(isOpen) => !isOpen && handleDeleteDialogClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('pages.tenants.deleteDialog.title', 'Delete Tenant')}</DialogTitle>
            <DialogDescription>
              {t('pages.tenants.confirmDelete', 'Are you sure you want to delete this tenant?')}
            </DialogDescription>
          </DialogHeader>

          {tenantToDelete?.commercialEntityCreatedWithTenant && tenantToDelete?.commercialEntityId ? (
            <div className="rounded-lg border p-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="deleteAlsoCommercial" className="font-medium">
                  {t('pages.tenants.deleteDialog.removeCommercialEntityLabel', 'Delete also from Clients/Suppliers')}
                </Label>
                <Switch
                  id="deleteAlsoCommercial"
                  checked={deleteAlsoCommercial}
                  onCheckedChange={(checked) => setDeleteAlsoCommercial(Boolean(checked))}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {t('pages.tenants.deleteDialog.removeCommercialEntityHint', 'This option is available because the linked client/supplier entry was auto-created with this tenant.')}
              </p>
              <p className="text-sm text-amber-600">
                {t('pages.tenants.deleteDialog.activeOrdersWarning', 'If active orders exist, the tenant can be deleted but the client/supplier entry will be kept to protect linked data.')}
              </p>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={handleDeleteDialogClose} disabled={deletingTenant}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deletingTenant}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deletingTenant ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.deleting', 'Deleting...')}
                </>
              ) : (
                t('pages.tenants.deleteDialog.confirmButton', 'Delete tenant')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedTenant ? t('pages.tenants.actions.edit') : t('pages.tenants.actions.create')}
            </DialogTitle>
            <DialogDescription>
              {selectedTenant
                ? t('pages.tenants.editDescription', 'Update tenant information')
                : t('pages.tenants.createDescription', 'Add a new organization tenant')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('pages.tenants.columns.name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('pages.tenants.namePlaceholder', 'Acme Corporation')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="logoUrl">{t('pages.tenants.columns.logoUrl')}</Label>
              <Input
                id="logoUrl"
                type="url"
                value={formData.logoUrl}
                onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                placeholder="https://example.com/logo.png"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primaryColor">{t('pages.tenants.columns.primaryColor')}</Label>
                <div className="flex gap-2">
                  <Input
                    id="primaryColor"
                    type="color"
                    value={formData.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    className="h-10 w-14 cursor-pointer p-1"
                  />
                  <Input
                    value={formData.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    className="flex-1 font-mono text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="secondaryColor">{t('pages.tenants.columns.secondaryColor')}</Label>
                <div className="flex gap-2">
                  <Input
                    id="secondaryColor"
                    type="color"
                    value={formData.secondaryColor}
                    onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                    className="h-10 w-14 cursor-pointer p-1"
                  />
                  <Input
                    value={formData.secondaryColor}
                    onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                    className="flex-1 font-mono text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="emailFatturazione">{t('pages.tenants.columns.email')}</Label>
              <Input
                id="emailFatturazione"
                type="email"
                value={formData.emailFatturazione}
                onChange={(e) => setFormData({ ...formData, emailFatturazione: e.target.value })}
                placeholder="billing@company.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="vatNumber">{t('pages.tenants.form.vatNumber', 'VAT Number')}</Label>
              <Input
                id="vatNumber"
                value={formData.vatNumber}
                onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
                placeholder={t('pages.tenants.form.vatNumberPlaceholder', 'e.g. IT12345678901')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pec">{t('pages.tenants.form.pec', 'PEC')}</Label>
              <Input
                id="pec"
                type="email"
                value={formData.pec}
                onChange={(e) => setFormData({ ...formData, pec: e.target.value })}
                placeholder={t('pages.tenants.form.pecPlaceholder', 'e.g. company@pec.it')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyAddress">{t('pages.tenants.form.companyAddress', 'Company Address')}</Label>
              <Input
                id="companyAddress"
                value={formData.companyAddress}
                onChange={(e) => setFormData({ ...formData, companyAddress: e.target.value })}
                placeholder={t('pages.tenants.form.companyAddressPlaceholder', 'Street, City, ZIP, Country')}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('pages.tenants.columns.language')}</Label>
              <Select
                value={formData.language}
                onValueChange={(value) => setFormData({ ...formData, language: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="it">Italiano</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving', 'Saving...')}
                </>
              ) : selectedTenant ? (
                t('common.update')
              ) : (
                t('common.create')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Management Dialog */}
      <Dialog open={userManagementOpen} onOpenChange={(isOpen) => !isOpen && setUserManagementOpen(false)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t('pages.tenants.userManagement.title', 'Manage Tenant Users')}
            </DialogTitle>
            <DialogDescription>
              {t('pages.tenants.userManagement.description', 'View and manage user roles for this tenant')}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {loadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
              </div>
            ) : selectedTenantUsers.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('pages.tenants.userManagement.columns.name', 'Name')}</TableHead>
                    <TableHead>{t('pages.tenants.userManagement.columns.email', 'Email')}</TableHead>
                    <TableHead>{t('pages.tenants.userManagement.columns.role', 'Role')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedTenantUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell className="text-muted-foreground">{user.email}</TableCell>
                      <TableCell>
                        <Select
                          value={pendingUserRoles[user.id] ?? user.role}
                          onValueChange={(newRole) =>
                            setPendingUserRoles((prev) => ({ ...prev, [user.id]: newRole }))
                          }
                          disabled={savingUserRoles}
                        >
                          <SelectTrigger className="w-[180px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="OPERATOR">{getRoleLabel('OPERATOR', t)}</SelectItem>
                            <SelectItem value="MANAGER">{getRoleLabel('MANAGER', t)}</SelectItem>
                            <SelectItem value="ACCOUNTANT">{getRoleLabel('ACCOUNTANT', t)}</SelectItem>
                            <SelectItem value="ADMIN">{getRoleLabel('ADMIN', t)}</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {t('pages.tenants.userManagement.noUsers', 'No users found for this tenant')}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUserManagementOpen(false)}>
              {t('common.close', 'Close')}
            </Button>
            <Button
              onClick={handleSaveUserRoles}
              disabled={savingUserRoles || !hasPendingRoleChanges}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {savingUserRoles ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving', 'Saving...')}
                </>
              ) : (
                t('common.save', 'Save')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{t('pages.tenants.statusDialog.title', 'Activation status')}</DialogTitle>
            <DialogDescription>
              {tenantStatus
                ? `${tenantStatus.tenantName} — ${t(`pages.tenants.statusDialog.overallStatus.${tenantStatus.overallStatus}`, tenantStatus.overallStatus)}`
                : t('pages.tenants.statusDialog.description', 'Current activation timeline for this tenant account')}
            </DialogDescription>
          </DialogHeader>

          {loadingStatus ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
            </div>
          ) : tenantStatus ? (
            <div className="space-y-4">
              <div className="rounded-lg border p-3">
                <p className="text-sm font-medium">
                  {t('pages.tenants.statusDialog.invitationEmail', 'Invitation email')}: {tenantStatus.invitation?.email || '—'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('pages.tenants.statusDialog.invitationState', 'Invitation state')}:{' '}
                  {t(
                    `pages.tenants.statusDialog.invitationStates.${tenantStatus.invitation?.state ?? 'not_sent'}`,
                    tenantStatus.invitation?.state ?? 'not_sent',
                  )}
                </p>
              </div>

              <div className="space-y-3">
                {tenantStatus.steps.map((step) => (
                  <div key={step.key} className="flex items-start justify-between gap-3 rounded-md border p-3">
                    <div>
                      <p className="text-sm font-medium">
                        {t(`pages.tenants.statusDialog.stepKeys.${step.key}`, step.title)}
                      </p>
                      <p className="text-xs text-muted-foreground">{formatDateTime(step.at)}</p>
                    </div>
                    <Badge variant={getStepBadgeVariant(step.status)}>
                      {t(`pages.tenants.statusDialog.stepStatus.${step.status}`, step.status)}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Alert variant="warning">
              {t('pages.tenants.statusDialog.empty', 'No activation status available')}
            </Alert>
          )}

          <DialogFooter>
            {tenantStatus && tenantStatus.overallStatus !== 'activated' && (
              <Button
                onClick={handleResendInvitation}
                disabled={resendingInvitation}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {resendingInvitation ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('pages.tenants.statusDialog.resending', 'Sending...')}
                  </>
                ) : (
                  t('pages.tenants.statusDialog.resendInvitation', 'Resend activation email')
                )}
              </Button>
            )}
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
              {t('common.close', 'Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTenantsList;
